import type { ReactNode } from 'react';
import { EMOJI_3D } from '../lib/emoji3d';

/** One emoji: a pictograph with its variation selector and any ZWJ-joined parts. */
const EMOJI_RUN = /\p{Extended_Pictographic}\uFE0F?(?:\u200D\p{Extended_Pictographic}\uFE0F?)*/gu;

/** File name of an emoji's picture: its code points in hex joined by '-', without FE0F (see gen-emoji3d.py). */
export function emojiKey(emoji: string): string {
  return [...emoji]
    .filter((c) => c !== '\uFE0F')
    .map((c) => (c.codePointAt(0) ?? 0).toString(16))
    .join('-');
}

/** The URL of an emoji's 3D picture, or null when the app has none (then the emoji stays text). */
export function emojiSrc(emoji: string): string | null {
  const key = emojiKey(emoji);
  return EMOJI_3D.has(key) ? `${import.meta.env.BASE_URL}emoji3d/${key}.webp` : null;
}

/**
 * A gift, sticker or decoration emoji drawn as its 3D picture (public/emoji3d), the same on every phone; emoji
 * without a picture stay text. It takes the font size of where it sits, like the emoji it replaces, unless `size`
 * (px) is given.
 */
export function Emoji({ e, size }: { e: string; size?: number }) {
  const src = emojiSrc(e);
  if (!src) return <>{e}</>;
  return (
    <img
      className="emoji3d"
      src={src}
      alt={e}
      style={size ? { width: size, height: size } : undefined}
      draggable={false}
      decoding="async"
    />
  );
}

/** Text with each emoji that has a 3D picture drawn as that picture; other emoji stay as typed. */
export function EmojiText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(EMOJI_RUN)) {
    if (!emojiSrc(m[0])) continue;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<Emoji key={m.index} e={m[0]} />);
    last = m.index + m[0].length;
  }
  if (!parts.length) return <>{text}</>;
  parts.push(text.slice(last));
  return <>{parts}</>;
}
